import { ComponentType, ReactNode } from "react";

export interface LinkButtonProps {
  /**
   * Linkコンポーネント（react-routerのLinkなど）
   */
  LinkComponent?: ComponentType<{ to: string; className?: string; children: ReactNode }>;

  /**
   * 外のサイトへのリンク。LinkComponent は使わず、href 付きの a を新しいタブで開く
   * (Link 系の部品は to しか受け取らず、a に渡すと href が落ちて押せなくなる)
   */
  external?: boolean;

  /**
   * リンク先
   */
  to: string;

  /**
   * ボタンのバリアント
   */
  variant?: "primary" | "primaryContrast" | "secondary" | "outline" | "ghost" | "black" | "outlineGray";

  /**
   * ボタンのサイズ
   */
  size?: "sm" | "md" | "lg";

  /**
   * ボタンの内容
   */
  children?: ReactNode;

  /**
   * 全幅表示
   */
  fullWidth?: boolean;

  /**
   * 追加のクラス名
   */
  className?: string;

  /**
   * 丸いボタン（pill形状）
   */
  rounded?: boolean;

  /**
   * Link要素へ引き継ぐdata属性
   */
  linkDataAttributes?: Partial<
    Record<`data-${string}`, string | number | boolean>
  >;
}

export function LinkButton({
  LinkComponent,
  external = false,
  to,
  variant = "primary",
  size = "md",
  children,
  fullWidth = false,
  className = "",
  rounded = false,
  linkDataAttributes,
}: LinkButtonProps) {
  // Base classes
  const baseClasses = `font-sans font-bold ${rounded ? "rounded-full" : "rounded-lg"} transition-all duration-200 outline-none inline-flex items-center justify-center gap-2 relative`;

  // 高さは Button と同じ下限 (小 36、通常 44、大 48)。無いと a だけ 30 前後になり、同じ部品で高さが割れる
  // Size variants
  const hasChildren = Boolean(children);
  const sizeClasses = {
    sm: hasChildren ? "min-h-9 px-3 py-1.5 max-sm:text-[0.9375rem] text-sm" : "p-1.5 max-sm:text-[0.9375rem] text-sm",
    md: hasChildren ? "min-h-11 px-5 py-2.5 max-sm:text-[0.9375rem] text-sm" : "p-2.5 max-sm:text-[0.9375rem] text-sm",
    lg: hasChildren ? "min-h-12 px-7 py-3.5 text-base" : "p-3.5 text-base",
  };

  // Variant classes with hover, active, focus states
  const variantClasses = {
    // **白文字を #05b45b に乗せると比 2.73 で、AA の 4.5 を大きく割る。**
    // ページでいちばん押してほしいボタンの文字が規格の6割しかなかった。
    // primary-600 (#037f40) なら 5.10 で通る。ダークでも 4.9 台に乗る
    primary:
      "bg-primary-600 text-white hover:bg-primary-700 active:bg-primary-600 focus:shadow-[0_0_0_2px_white,0_0_0_4px_rgba(34,197,94,0.5)]",
    primaryContrast:
      "bg-primary-600 text-white hover:bg-primary-700 active:bg-primary-800 focus:shadow-[0_0_0_2px_white,0_0_0_4px_rgba(34,197,94,0.5)]",
    secondary:
      "bg-second text-primary border border-primary hover:bg-third active:bg-third focus:shadow-[0_0_0_2px_white,0_0_0_4px_rgba(34,197,94,0.5)]",
    outline:
      "bg-transparent text-primary border border-primary hover:bg-main hover:border-primary active:bg-third active:border-primary focus:shadow-[0_0_0_2px_white,0_0_0_4px_rgba(34,197,94,0.5)] focus:border-primary",
    outlineGray:
      "bg-transparent text-sub border border-gray-200 hover:bg-main hover:border-gray-200 active:bg-third active:border-gray-200 focus:shadow-[0_0_0_2px_white,0_0_0_4px_rgba(0.0,0.0,0.0,0.05)] focus:border-gray-200",
    ghost:
      "bg-transparent text-sub hover:bg-second active:bg-third focus:shadow-[0_0_0_2px_white,0_0_0_4px_rgba(34,197,94,0.5)]",
    black:
      "bg-black text-white hover:bg-black/90 active:bg-black/90 focus:shadow-[0_0_0_2px_white,0_0_0_4px_rgba(0,0,0,0.5)]",
  };

  // Width classes
  const widthClass = fullWidth ? "w-full" : "w-auto";

  const linkClasses = `${baseClasses} ${sizeClasses[size]} ${variantClasses[variant]} ${widthClass} ${className}`;

  if (external || !LinkComponent) {
    return (
      <a
        {...linkDataAttributes}
        href={to}
        target="_blank"
        rel="noopener noreferrer"
        className={linkClasses}
      >
        {children}
      </a>
    );
  }

  return (
    <LinkComponent
      {...linkDataAttributes}
      to={to}
      className={linkClasses}
    >
      {children}
    </LinkComponent>
  );
}
